"use client";

import React, { useState, useEffect } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiMoonBold, PiSunBold } from "react-icons/pi";
import { getImageUrl } from "@/utils/image-url";

export default function OvanHeader({
  content,
  onContentChange,
  isSelected,
  defaultLogo,
}: SectionContentProps) {
  const fontFamily = content.fontFamily || "'Inter', sans-serif";
  const logoText = content.logoText || "Instacks";

  const [isDark, setIsDark] = useState(true);
  const [customLogo, setCustomLogo] = useState<string | null>(null);
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedTheme = localStorage.getItem("ovan-theme") || "dark";
      setIsDark(savedTheme === "dark");

      // Sync class initially
      if (savedTheme === "dark") {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }

      // Check for logo inside localStorage
      const userLogo = localStorage.getItem("cached_my_logo") || localStorage.getItem("brand_logo") || localStorage.getItem("logo");
      if (userLogo) {
        setCustomLogo(userLogo);
      } else {
        setCustomLogo(null);
      }

      const handleThemeChange = () => {
        setIsDark(localStorage.getItem("ovan-theme") === "dark");
      };

      const handleLogoChange = () => {
        setCustomLogo(localStorage.getItem("cached_my_logo") || localStorage.getItem("brand_logo") || localStorage.getItem("logo"));
      };

      window.addEventListener("ovan-theme-change", handleThemeChange);
      window.addEventListener("storage", handleLogoChange);
      return () => {
        window.removeEventListener("ovan-theme-change", handleThemeChange);
        window.removeEventListener("storage", handleLogoChange);
      };
    }
  }, []);

  const changeTheme = (themeMode: "dark" | "light") => {
    localStorage.setItem("ovan-theme", themeMode);
    setIsDark(themeMode === "dark");
    window.dispatchEvent(new Event("ovan-theme-change"));

    if (themeMode === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  };

  const defaultNavItems = [
    { label: "Home", href: "#hero", sectionId: "hero" },
    { label: "About", href: "#about", sectionId: "about" },
    { label: "Features", href: "#features", sectionId: "features" },
    { label: "Services", href: "#services", sectionId: "services" },
    { label: "Team", href: "#team", sectionId: "team" },
    { label: "Contact", href: "#contact", sectionId: "contact" }
  ];

  const navItems = content.navItems || defaultNavItems;

  const isValidImageUrl = (url: string) => {
    if (!url) return false;
    return (
      url.startsWith("http") ||
      url.startsWith("data:") ||
      url.startsWith("/") ||
      url.startsWith("blob:") ||
      url.length > 50
    );
  };

  const renderTextLogo = (text: string) => (
    <span className={`text-2xl font-bold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
      {text}
    </span>
  );

  return (
    <>
      {/* Top Hiring Tagline Bar */}
      {/* <div className={`w-full py-2.5 transition-colors duration-200 border-b ${
        isDark 
          ? "bg-slate-850 border-slate-800/80 text-slate-400" 
          : "bg-blue-50/30 border-blue-50/50 text-slate-600"
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center text-xs font-nunito tracking-wide">
            <span></span>
            <span className="font-medium flex items-center gap-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse"></span>
              We are hiring team member now!
            </span>
            <span className="font-bold text-blue-600 flex items-center gap-0.5">
              Join us &gt;
            </span>
          </div>
        </div>
      </div> */}
      <header
        className={`w-full transition-all duration-200 z-50 ${isDark ? "bg-slate-900 text-white border-b border-slate-800" : "bg-white text-slate-900 border-b border-slate-100"
          } ${isSelected ? "ring-2 ring-blue-500 relative z-20 shadow-md" : ""
          }`}
        style={{ fontFamily }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            {/* Instacks Logo */}
            <div className="flex-shrink-0 flex items-center">
              <a href="/" className="logo flex items-center">
                {content.logo && isValidImageUrl(content.logo) && !logoFailed ? (
                  <img
                    className="h-8 w-auto inline-block"
                    alt="Brand Logo"
                    src={getImageUrl(content.logo) || content.logo}
                    onError={() => setLogoFailed(true)}
                  />
                ) : defaultLogo && isValidImageUrl(defaultLogo) && !logoFailed ? (
                  <img
                    className="h-8 w-auto inline-block"
                    alt="Brand Logo"
                    src={getImageUrl(defaultLogo) || defaultLogo}
                    onError={() => setLogoFailed(true)}
                  />
                ) : customLogo && isValidImageUrl(customLogo) && !logoFailed ? (
                  <img
                    className="h-8 w-auto inline-block"
                    alt="Custom Brand Logo"
                    src={getImageUrl(customLogo) || customLogo}
                    onError={() => setLogoFailed(true)}
                  />
                ) : content.logo && content.logo.trim() !== "" && !isValidImageUrl(content.logo) ? (
                  renderTextLogo(content.logo)
                ) : defaultLogo && defaultLogo.trim() !== "" && !isValidImageUrl(defaultLogo) ? (
                  renderTextLogo(defaultLogo)
                ) : logoText && logoText.trim() !== "" ? (
                  renderTextLogo(logoText)
                ) : !isDark ? (
                  <img
                    className="h-8 w-auto inline-block"
                    alt="Instacks Logo"
                    src="/assets/logo.svg"
                  />
                ) : (
                  <img
                    className="h-8 w-auto inline-block"
                    alt="Instacks Logo"
                    src="/assets/logo-white.svg"
                  />
                )}
              </a>
            </div>

            {/* Nav Menu */}
            <nav className="hidden md:flex space-x-8">
              {navItems.map((item: any, idx: number) => {
                const isCustomLink = item.sectionId === "custom" || (!item.sectionId && item.href && !item.href.startsWith("#"));
                const targetHref = item.sectionId && item.sectionId !== "custom" 
                  ? `#${item.sectionId}` 
                  : (item.url || item.href || "#");
                const isEditing = !!onContentChange;

                return (
                  <a
                    key={idx}
                    href={isEditing ? undefined : targetHref}
                    target={isCustomLink && !isEditing ? "_blank" : undefined}
                    rel={isCustomLink ? "noopener noreferrer" : undefined}
                    className={`text-sm font-semibold transition-colors ${
                      isDark ? "text-slate-300 hover:text-blue-400" : "text-slate-700 hover:text-blue-600"
                    }`}
                    onClick={(e) => {
                      if (isEditing) {
                        e.preventDefault();
                      } else if (!isCustomLink) {
                        e.preventDefault();
                        const targetId = item.sectionId || (item.href && item.href.startsWith("#") ? item.href.substring(1) : "");
                        if (targetId) {
                          const el = document.getElementById(targetId);
                          if (el) {
                            el.scrollIntoView({ behavior: "smooth" });
                          }
                        }
                      }
                    }}
                  >
                    {item.label}
                  </a>
                );
              })}
            </nav>

            {/* Action Buttons */}
            <div className="flex items-center gap-4">
              {/* Theme Toggle - replaces search icon */}
              <button
                onClick={() => changeTheme(isDark ? "light" : "dark")}
                className={`p-2 rounded-lg transition-all duration-200 ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-slate-900"}`}
                title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                {isDark ? <PiSunBold className="size-5" /> : <PiMoonBold className="size-5" />}
              </button>
              <a
                href="/login"
                className={`hidden sm:inline-flex items-center justify-center px-6 py-2.5 text-sm font-semibold rounded-full transition-all duration-300 border ${isDark ? "text-white bg-transparent hover:bg-white hover:text-slate-900 border-white" : "text-slate-900 bg-transparent hover:bg-slate-900 hover:text-white border-slate-900"
                  }`}
              >
                Login
              </a>
            </div>
          </div>
        </div>
      </header>


    </>
  );
}
